// Zcash adapter — transparent (t-address) keys and derivation only.
//
// The chain is NOT registered in SUPPORTED_CHAINS: reading a balance needs an
// address-indexed data source we do not have yet, and a chain that appears in
// the wallet with no balance and no deposits is worse than one that does not
// appear. This is the half that needs no infrastructure.
//
// Transparent Zcash is Bitcoin's key machinery with a different address
// version: secp256k1, BIP-44 derivation, a compressed public key hashed to 20
// bytes, base58check. What differs is a two-byte version prefix instead of
// one, which is also why a Bitcoin address decoder accepts a t-address's
// checksum and then reads the wrong hash.
//
// Shielded addresses are a different system entirely — Sapling/Orchard keys
// under ZIP-32, with their own derivation and their own encoding — and are
// deliberately out of scope here. A transparent-only Zcash wallet gives none
// of the privacy the chain is used for, so this is a stepping stone, not the
// destination.

import { HDKey } from '@scure/bip32';
import { base58check } from '@scure/base';
import { sha256 } from '@noble/hashes/sha256';
import { ripemd160 } from '@noble/hashes/ripemd160';
import type { DerivedAddresses } from '../types';

const b58check = base58check(sha256);

/** SLIP-44 coin type 133, confirmed against Trezor's coin definition. */
const PATH = (index: number) => `m/44'/133'/0'/0/${index}`;

/**
 * Mainnet version prefixes, two bytes each — read from zcashd's chainparams
 * and cross-checked against Trezor's coin definition (7352 = 0x1CB8,
 * 7357 = 0x1CBD).
 */
const T_ADDRESS_P2PKH = Uint8Array.of(0x1c, 0xb8); // t1…
const T_ADDRESS_P2SH = Uint8Array.of(0x1c, 0xbd); // t3…

/** Named `eoa` like every other adapter's, so the wallet can key derivation by
 *  chain without a per-chain shape. */
export type ZcashAddresses = DerivedAddresses;

/** hash160 — the same 20 bytes Bitcoin puts in a P2PKH address. */
function hash160(data: Uint8Array): Uint8Array {
  return ripemd160(sha256(data));
}

/** base58check over a two-byte version prefix and a 20-byte hash. */
export function encodeZcashTransparent(hash: Uint8Array, version = T_ADDRESS_P2PKH): string {
  if (hash.length !== 20) throw new Error('zcash: expected a 20-byte hash');
  const payload = new Uint8Array(version.length + hash.length);
  payload.set(version);
  payload.set(hash, version.length);
  return b58check.encode(payload);
}

/** Derive the transparent address and public key at the given account index. */
export function deriveZcashAddresses(seed: Uint8Array, index = 0): ZcashAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.publicKey) throw new Error('Zcash derivation failed');
  return {
    eoa: encodeZcashTransparent(hash160(child.publicKey)),
    publicKey: Buffer.from(child.publicKey).toString('hex'),
  };
}

/** The signing key for an account index. Transparent only: secp256k1, the same
 *  key a Bitcoin P2PKH spend would use. */
export function zcashPrivateKey(seed: Uint8Array, index = 0): Uint8Array {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.privateKey) throw new Error('Zcash derivation failed');
  return child.privateKey;
}

/**
 * Whether a string is a well-formed transparent mainnet address.
 *
 * Checks the version prefix rather than the leading characters: "t1" and "t3"
 * fall out of the version bytes, and matching on the text would accept a
 * Bitcoin address that happens to encode to something starting with t.
 *
 * Shielded addresses (zs…, u…) are rejected. We cannot derive or spend them,
 * so accepting one as a recipient would send funds we could not help recover.
 */
export function isValidZcashAddress(address: string): boolean {
  let decoded: Uint8Array;
  try {
    decoded = b58check.decode(address);
  } catch {
    return false;
  }
  if (decoded.length !== 22) return false;
  for (const version of [T_ADDRESS_P2PKH, T_ADDRESS_P2SH]) {
    if (decoded[0] === version[0] && decoded[1] === version[1]) return true;
  }
  return false;
}

/** The 20-byte hash inside a transparent address, for comparing an address
 *  against a public key without re-encoding it. */
export function zcashTransparentHash(address: string): Uint8Array {
  const decoded = b58check.decode(address);
  if (decoded.length !== 22) throw new Error('zcash: not a transparent address');
  return decoded.subarray(2);
}
